import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, onSnapshot, where, writeBatch, doc, serverTimestamp, deleteDoc, updateDoc, getDocs, addDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import VendorFormModal from './VendorFormModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import ContractManagementModal from './ContractManagementModal';
import Papa from 'papaparse';
import { CSVLink } from 'react-csv';
import { vendorRejectedTemplate, sendNotificationEmail, statusChangeTemplate } from '../../../firebase/notifications';
import { EditIcon, TrashIcon, FileTextIcon } from '../../../components/icons/Icons';

// Helper function to generate a random 12-character code
const generateAccessCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < 12; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

// --- FIX #1: Add this helper function at the top of the file ---
// This function safely converts Firestore Timestamps, JS Dates, or date strings into a comparable Date object.
const convertToDate = (timestamp) => {
    if (!timestamp) return null;

    // 1. Check for a standard Firestore Timestamp
    if (typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
    }

    // 2. Check for a "corrupted" timestamp (a plain object)
    if (timestamp.seconds && typeof timestamp.seconds === 'number') {
        // Convert the seconds to milliseconds and create a new date
        return new Date(timestamp.seconds * 1000);
    }

    // 3. Try to parse it as a string, number, or JS Date
    const d = new Date(timestamp);
    if (!isNaN(d.getTime())) {
        return d;
    }
    
    // If all else fails, return null
    return null; 
};

// Invite Modal now handles both new and existing vendor requests
function InviteVendorModal({ appId, onClose, existingVendor = null }) {
    const [vendorName, setVendorName] = useState('');
    const [vendorEmail, setVendorEmail] = useState('');
    const [accessCode, setAccessCode] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        setAccessCode(generateAccessCode());
        if (existingVendor) {
            setVendorName(existingVendor.companyName || '');
            setVendorEmail(existingVendor.email || '');
        }
    }, [existingVendor]);

    const handleSendInvite = async () => {
        if (!vendorName || !vendorEmail || !accessCode) {
            setMessage('Please fill all fields.');
            return;
        }
        setIsSaving(true);
        setMessage('');
        try {
            if (existingVendor) {
                const vendorRef = doc(db, `/artifacts/${appId}/public/data/vendors`, existingVendor.id);
                await updateDoc(vendorRef, {
                    inviteCode: accessCode,
                    status: 'Invited',
                    inviteStatus: 'Active',
                });
                setMessage(`Invite for ${vendorName} sent successfully.`);
            } else {
                const vendorsCollection = collection(db, `/artifacts/${appId}/public/data/vendors`);
                await addDoc(vendorsCollection, {
                    companyName: vendorName,
                    email: vendorEmail,
                    inviteCode: accessCode,
                    status: 'Invited',
                    inviteStatus: 'Active',
                    createdAt: serverTimestamp(),
                    submittedBy: 'Finance Invite'
                });
                setMessage(`Invite for ${vendorName} created. Remember to send them the form link and their access code.`);
            }
            setTimeout(onClose, 3000);
        } catch (error) {
            console.error("Error sending invite:", error);
            setMessage(error.message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
                <h2 className="text-xl font-bold text-[#213368] mb-4">Invite Vendor</h2>
                <p className="text-sm text-gray-600 mb-4">
                    A unique access code has been generated. You must send this code to the vendor separately along with the link to the public form.
                </p>
                <div className="space-y-4">
                     <input value={vendorName} onChange={(e) => setVendorName(e.target.value)} placeholder="Vendor Company Name" className="w-full p-2 border rounded" readOnly={!!existingVendor} />
                     <input type="email" value={vendorEmail} onChange={(e) => setVendorEmail(e.target.value)} placeholder="Vendor Email Address" className="w-full p-2 border rounded" readOnly={!!existingVendor} />
                     <div className="flex items-center space-x-2">
                        <input value={accessCode} readOnly placeholder="Access Code" className="w-full p-2 border rounded bg-gray-100 font-mono" />
                        <button type="button" onClick={() => setAccessCode(generateAccessCode())} className="px-3 py-2 bg-gray-200 text-sm rounded-md hover:bg-gray-300">Generate</button>
                     </div>
                </div>
                 {message && <p className="mt-4 text-sm text-center">{message}</p>}
                <div className="flex justify-end space-x-4 mt-6">
                    <button onClick={onClose} disabled={isSaving} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={handleSendInvite} disabled={isSaving} className="px-6 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700">Send Invite</button>
                </div>
            </div>
        </div>
    );
}

// Main component for Vendor Management
export default function VendorManagement({ appId, userProfile }) {
    const [activeTab, setActiveTab] = useState('requests');
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
    const [selectedVendor, setSelectedVendor] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [vendorToDelete, setVendorToDelete] = useState(null);
    const [isContractModalOpen, setIsContractModalOpen] = useState(false);
    const [vendorForContracts, setVendorForContracts] = useState(null);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [vendorToReject, setVendorToReject] = useState(null);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [vendorToInvite, setVendorToInvite] = useState(null);

    const handleOpenForm = (vendor = null) => {
        setSelectedVendor(vendor);
        setIsFormOpen(true);
    };

    const handleOpenDeleteModal = (vendor) => {
        setVendorToDelete(vendor);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!vendorToDelete) return;
        try {
            const vendorRef = doc(db, `/artifacts/${appId}/public/data/vendors`, vendorToDelete.id);
            await deleteDoc(vendorRef);
        } catch (error) {
            console.error("Error deleting vendor:", error);
            alert("Failed to delete vendor.");
        } finally {
            setIsDeleteModalOpen(false);
            setVendorToDelete(null);
        }
    };

    const handleOpenContractModal = (vendor) => {
        setVendorForContracts(vendor);
        setIsContractModalOpen(true);
    };

    const handleRejectVendor = async (vendor, reason) => {
        if (!vendor || !reason) {
            alert("A reason is required for rejection.");
            return;
        }
        try {
            const batch = writeBatch(db);
            const vendorRef = doc(db, `/artifacts/${appId}/public/data/vendors`, vendor.id);
            batch.update(vendorRef, { status: 'Rejected', rejectionReason: reason });

            if (vendor.purchaseRequestId) {
                const prRef = doc(db, `/artifacts/${appId}/public/data/purchaseRequests`, vendor.purchaseRequestId);
                const prDoc = await getDoc(prRef);
                if (prDoc.exists()) {
                    const prData = prDoc.data();
                    batch.update(prRef, {
                        status: 'Rejected',
                        approvalComments: `Vendor rejected by Finance: ${reason}`
                    });

                    const requesterRef = doc(db, `/artifacts/${appId}/users`, prData.requesterId);
                    const requesterSnap = await getDoc(requesterRef);
                    if (requesterSnap.exists()) {
                        const emailContent = statusChangeTemplate(prData, 'Rejected');
                        await sendNotificationEmail({
                            to: [requesterSnap.data().email],
                            ...emailContent,
                            appId
                        });
                    }
                }
            }

            await batch.commit();
            setIsRejectModalOpen(false);

        } catch (error) {
            console.error("Error rejecting vendor:", error);
            alert("Failed to reject vendor.");
        }
    };

    const handleOpenInviteModal = (vendor = null) => {
        setVendorToInvite(vendor);
        setIsInviteModalOpen(true);
    };

    return (
        <>
            <div className="flex justify-between items-center mb-4">
                <div>
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            <button onClick={() => setActiveTab('requests')} className={`${activeTab === 'requests' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500 hover:text-gray-700'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                                New Vendor Requests
                            </button>
                            <button onClick={() => setActiveTab('database')} className={`${activeTab === 'database' ? 'border-[#20BEC6] text-[#20BEC6]' : 'border-transparent text-gray-500 hover:text-gray-700'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}>
                                Master Vendor Database
                            </button>
                        </nav>
                    </div>
                </div>
                {activeTab === 'database' && (
                     <div className="space-x-2">
                        <button onClick={() => handleOpenInviteModal()} className="px-4 py-2 bg-teal-600 text-white font-semibold rounded-lg shadow-md hover:bg-teal-700">
                            Invite Vendor
                        </button>
                        <button onClick={() => setIsBulkUploadOpen(true)} className="px-4 py-2 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700">
                            Bulk Upload
                        </button>
                         <button onClick={() => handleOpenForm()} className="px-4 py-2 bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:bg-blue-700">
                            Add Manually
                        </button>
                    </div>
                )}
            </div>

            {activeTab === 'requests' && <VendorRequestsView appId={appId} onOnboard={handleOpenForm} onReject={(vendor) => { setVendorToReject(vendor); setIsRejectModalOpen(true); }} onInvite={handleOpenInviteModal} />}
            {activeTab === 'database' && <VendorDatabaseView appId={appId} onEdit={handleOpenForm} onDelete={handleOpenDeleteModal} onManageContracts={handleOpenContractModal} />}

            {isFormOpen && <VendorFormModal appId={appId} userProfile={userProfile} existingVendor={selectedVendor} onClose={() => setIsFormOpen(false)} />}
            {isBulkUploadOpen && <BulkUploadModal appId={appId} onClose={() => setIsBulkUploadOpen(false)} />}
            {isDeleteModalOpen && (
                <DeleteConfirmModal
                    vendorName={vendorToDelete?.basicInfo?.legalName || vendorToDelete?.companyName || 'this vendor'}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={handleConfirmDelete}
                />
            )}
            {isContractModalOpen && (
                <ContractManagementModal
                    vendor={vendorForContracts}
                    appId={appId}
                    onClose={() => setIsContractModalOpen(false)}
                />
            )}
            {isRejectModalOpen && (
                <RejectVendorModal
                    vendor={vendorToReject}
                    onClose={() => setIsRejectModalOpen(false)}
                    onReject={handleRejectVendor}
                />
            )}
            {isInviteModalOpen && <InviteVendorModal appId={appId} onClose={() => setIsInviteModalOpen(false)} existingVendor={vendorToInvite} />}
        </>
    );
}

function VendorRequestsView({ appId, onOnboard, onReject, onInvite }) {
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const q = query(
            collection(db, `/artifacts/${appId}/public/data/vendors`),
            where("status", "in", ["Invited", "Pending Finance Review", "Rejected", "Pending Approval"])
        );
        const unsub = onSnapshot(q, (snapshot) => {
            const sortedData = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .sort((a, b) => (convertToDate(b.createdAt) || 0) - (convertToDate(a.createdAt) || 0));
            setRequests(sortedData);
            setIsLoading(false);
        });
        return () => unsub();
    }, [appId]);

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Invited':
                return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">Invited</span>;
            case 'Pending Finance Review':
                 return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">Pending Review</span>;
            case 'Rejected':
                return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Rejected</span>;
            case 'Pending Approval':
                return <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">Needs Action</span>;
            default:
                return null;
        }
    };

    if (isLoading) return <LoadingSpinner />;
    if (requests.length === 0) return <p className="text-center text-gray-500 py-8 bg-white rounded-lg shadow-md">No vendor requests requiring action.</p>

    return (
        /* FIX 1: Changed 'overflow-hidden' to 'overflow-x-auto' to enable horizontal scroll if needed */
        <div className="bg-white shadow-md rounded-lg overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Access Code</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Actions</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {requests.map(vendor => (
                        <tr key={vendor.id}>
                            {/* FIX 2: Replaced 'whitespace-nowrap' with 'max-w-xs truncate' & added full name tooltip on hover */}
                            <td 
                                className="px-6 py-4 text-sm font-medium text-gray-900 max-w-xs truncate" 
                                title={vendor.companyName}
                            >
                                {vendor.companyName}
                            </td>
                            <td 
                                className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" 
                                title={vendor.email}
                            >
                                {vendor.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-600">{vendor.inviteCode || 'N/A'}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">{getStatusBadge(vendor.status)}</td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                                {vendor.status === 'Pending Approval' && (
                                    <>
                                        <button
                                            onClick={() => onInvite(vendor)}
                                            className="px-3 py-1 text-xs bg-teal-500 text-white rounded-md hover:bg-teal-600"
                                        >
                                            Invite
                                        </button>
                                        <button
                                            onClick={() => onOnboard(vendor)}
                                            className="px-3 py-1 text-xs bg-blue-500 text-white rounded-md hover:bg-blue-600"
                                        >
                                            Add Manually
                                        </button>
                                    </>
                                )}
                                {vendor.status === 'Pending Finance Review' && (
                                    <>
                                        <button
                                            onClick={() => onOnboard(vendor)}
                                            className="px-3 py-1 text-xs bg-green-500 text-white rounded-md hover:bg-green-600"
                                        >
                                            Review & Onboard
                                        </button>
                                        <button
                                            onClick={() => onReject(vendor)}
                                            className="px-3 py-1 text-xs bg-red-500 text-white rounded-md hover:bg-red-600"
                                        >
                                            Reject
                                        </button>
                                    </>
                                )}
                                {vendor.status === 'Invited' && (
                                    <div title="Waiting for vendor to submit their details.">
                                        <button
                                            className="px-3 py-1 text-xs bg-gray-300 text-white rounded-md cursor-not-allowed"
                                            disabled
                                        >
                                            Review & Onboard
                                        </button>
                                    </div>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function VendorDatabaseView({ appId, onEdit, onDelete, onManageContracts }) {
    const [vendors, setVendors] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'legalName', direction: 'ascending' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);

    useEffect(() => {
        const q = query(collection(db, `/artifacts/${appId}/public/data/vendors`), where("status", "==", "Active"));
        const unsub = onSnapshot(q, (snapshot) => {
            setVendors(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            setIsLoading(false);
        });
        return () => unsub();
    }, [appId]);

    const sortedAndFilteredVendors = useMemo(() => {
        let filtered = [...vendors];
        if (searchTerm) {
            const lowercasedTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(vendor =>
                vendor.basicInfo?.legalName?.toLowerCase().includes(lowercasedTerm) ||
                vendor.basicInfo?.email?.toLowerCase().includes(lowercasedTerm) ||
                vendor.basicInfo?.contactPerson?.toLowerCase().includes(lowercasedTerm)
            );
        }

        filtered.sort((a, b) => {
            const getSortValue = (vendor, key) => {
                switch(key) {
                    case 'legalName': return vendor.basicInfo?.legalName || '';
                    case 'contactPerson': return vendor.basicInfo?.contactPerson || '';
                    case 'country': return vendor.corporateInfo?.country || '';
// --- FIX IS HERE ---
                    // Use the safe convertToDate helper function for sorting
                    case 'dateAdded': return convertToDate(vendor.createdAt) || new Date(0);                    default: return '';
                }
            };

            let aValue = getSortValue(a, sortConfig.key);
            let bValue = getSortValue(b, sortConfig.key);

            if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        });

        return filtered;
    }, [vendors, searchTerm, sortConfig]);

    const paginatedVendors = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return sortedAndFilteredVendors.slice(startIndex, startIndex + itemsPerPage);
    }, [sortedAndFilteredVendors, currentPage, itemsPerPage]);

    const totalPages = Math.ceil(sortedAndFilteredVendors.length / itemsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
        setCurrentPage(1);
    };
    
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(sortedAndFilteredVendors.map(v => v.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (e, id) => {
        if (e.target.checked) {
            setSelectedIds(prev => [...prev, id]);
        } else {
            setSelectedIds(prev => prev.filter(selectedId => selectedId !== id));
        }
    };

    const getCsvData = () => {
        const selectedVendors = vendors.filter(v => selectedIds.includes(v.id));
        return selectedVendors.map(v => {
	const dateAdded = convertToDate(v.createdAt);            
	return { 
	    "Legal Name": v.basicInfo?.legalName,
            "Address": v.basicInfo?.address,
            "Contact Person": v.basicInfo?.contactPerson,
            "Phone": v.basicInfo?.phone,
            "Email": v.basicInfo?.email,
            "Website": v.basicInfo?.website,
            "Company Description": v.corporateInfo?.description,
            "Entity Type": v.corporateInfo?.entityType,
            "Registration Number": v.corporateInfo?.registrationNumber,
            "PAN": v.corporateInfo?.pan,
            "GST": v.corporateInfo?.gst,
            "Country": v.corporateInfo?.country,
            "Bank Name": v.bankingInfo?.bankName,
            "Account Number": v.bankingInfo?.accountNumber,
            "Beneficiary Name": v.bankingInfo?.beneficiaryName,
            "Branch Address": v.bankingInfo?.branchAddress,
            "Branch Code": v.bankingInfo?.branchCode,
            "SWIFT": v.bankingInfo?.swift,
            "Payment Email": v.bankingInfo?.paymentEmail,
            "Special Instructions": v.bankingInfo?.specialInstructions,
            "Date Added": convertToDate(v.createdAt)?.toLocaleDateString() || "",
            "Status": v.status,
           };
	});
    };

    const csvHeaders = [
        { label: "Legal Name", key: "Legal Name" }, { label: "Address", key: "Address" },
        { label: "Contact Person", key: "Contact Person" }, { label: "Phone", key: "Phone" },
        { label: "Email", key: "Email" }, { label: "Website", key: "Website" },
        { label: "Company Description", key: "Company Description" }, { label: "Entity Type", key: "Entity Type" },
        { label: "Registration Number", key: "Registration Number" }, { label: "PAN", key: "PAN" },
        { label: "GST", key: "GST" }, { label: "Country", key: "Country" },
        { label: "Bank Name", key: "Bank Name" }, { label: "Account Number", key: "Account Number" },
        { label: "Beneficiary Name", key: "Beneficiary Name" }, { label: "Branch Address", key: "Branch Address" },
        { label: "Branch Code", key: "Branch Code" }, { label: "SWIFT", key: "SWIFT" },
        { label: "Payment Email", key: "Payment Email" }, { label: "Special Instructions", key: "Special Instructions" },
        { label: "Date Added", key: "Date Added" }, { label: "Status", key: "Status" },
    ];

    if (isLoading) return <LoadingSpinner />;

    return (
        <div>
            <div className="flex justify-between items-center mb-4">
                <input
                    type="text"
                    placeholder="Search by Legal Name, Email, or Contact Person..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="w-1/2 p-2 border border-gray-300 rounded-lg shadow-sm"
                />
                {selectedIds.length > 0 && (
                    <CSVLink
                        data={getCsvData()}
                        headers={csvHeaders}
                        filename={`vendor_export_${new Date().toISOString().split('T')[0]}.csv`}
                        className="px-4 py-2 bg-gray-600 text-white font-semibold rounded-lg shadow-md hover:bg-gray-700"
                    >
                        Download CSV ({selectedIds.length})
                    </CSVLink>
                )}
            </div>

            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3"><input type="checkbox" onChange={handleSelectAll} /></th>
                            <th onClick={() => requestSort('legalName')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Legal Name</th>
                            <th onClick={() => requestSort('contactPerson')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Contact Person</th>
                            <th onClick={() => requestSort('country')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Country</th>
                            <th onClick={() => requestSort('dateAdded')} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer">Date Added</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {paginatedVendors.map(vendor => (
                            <tr key={vendor.id}>
                                <td className="px-6 py-4"><input type="checkbox" checked={selectedIds.includes(vendor.id)} onChange={(e) => handleSelectOne(e, vendor.id)} /></td>
                                <td className="px-6 py-4 whitespace-nowrap">{vendor.basicInfo?.legalName}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{vendor.basicInfo?.contactPerson}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{vendor.corporateInfo?.country || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap">{convertToDate(vendor.createdAt)?.toLocaleDateString() || 'N/A'}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="flex items-center space-x-4">
                                        <button onClick={() => onEdit(vendor)} className="tooltip-container text-yellow-600 hover:text-yellow-800" aria-label="Edit Vendor">
                                            <EditIcon />
                                            <span className="tooltip">Edit Vendor</span>
                                        </button>
                                        <button onClick={() => onManageContracts(vendor)} className="tooltip-container text-blue-600 hover:text-blue-800" aria-label="Manage Contracts">
                                            <FileTextIcon />
                                            <span className="tooltip">Manage Contracts</span>
                                        </button>
                                        <button onClick={() => onDelete(vendor)} className="tooltip-container" aria-label="Delete Vendor">
                                            <TrashIcon />
                                            <span className="tooltip">Delete Vendor</span>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            
            {totalPages > 1 && (
                <div className="flex justify-between items-center mt-4">
                    <div className="flex items-center space-x-2 text-sm">
                        <span>Items per page:</span>
                        <select value={itemsPerPage} onChange={handleItemsPerPageChange} className="border border-gray-300 rounded-md p-1">
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Previous
                        </button>
                        <span className="text-sm">
                            Page {currentPage} of {totalPages}
                        </span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="px-3 py-1 border rounded-md disabled:opacity-50">
                            Next
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

function BulkUploadModal({ appId, onClose }) {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [message, setMessage] = useState('');

    const handleFileChange = (e) => {
        setFile(e.target.files[0]);
        setMessage('');
    };
    
    const handleUpload = () => {
        if (!file) {
            setMessage('Please select a file first.');
            return;
        }
        setIsUploading(true);
        setMessage('Processing file...');

        const headerMapping = {
            "Legal Name": "legalName", "Address": "address", "Contact Person": "contactPerson",
            "Phone": "phone", "Email": "email", "Website": "website", "Company Description": "description",
            "Entity Type": "entityType", "Registration Number": "registrationNumber", "PAN": "pan",
            "GST": "gst", "Country": "country", "Bank Name": "bankName", "Account Number": "accountNumber",
            "Beneficiary Name": "beneficiaryName", "Branch Address": "branchAddress", "Branch Code": "branchCode",
            "SWIFT": "swift", "Payment Email": "paymentEmail", "Special Instructions": "specialInstructions",
        };

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                try {
                    setMessage('Checking for duplicates in the database...');
                    const existingVendorsQuery = query(collection(db, `/artifacts/${appId}/public/data/vendors`), where("status", "==", "Active"));
                    const querySnapshot = await getDocs(existingVendorsQuery);
                    const existingNames = new Set(querySnapshot.docs.map(doc => doc.data().basicInfo?.legalName?.toLowerCase()));

                    const batch = writeBatch(db);
                    const processedNames = new Set();
                    let successfulCount = 0;
                    let skippedCount = 0;

                    for (const row of results.data) {
                        const vendor = {};
                        for (const key in headerMapping) {
                            if (row[key] !== undefined && row[key] !== null) {
                                vendor[headerMapping[key]] = row[key];
                            }
                        }

                        const vendorName = vendor.legalName?.toLowerCase();

                        if (!vendor.legalName || !vendor.email) {
                            skippedCount++;
                            continue;
                        }
                        if (existingNames.has(vendorName) || processedNames.has(vendorName)) {
                            skippedCount++;
                            continue;
                        }

                        processedNames.add(vendorName);
                        successfulCount++;
                        
                        const vendorRef = doc(collection(db, `/artifacts/${appId}/public/data/vendors`));
                        
                        batch.set(vendorRef, {
                            basicInfo: {
                                legalName: vendor.legalName, contactPerson: vendor.contactPerson || null,
                                email: vendor.email, searchableName: vendor.legalName.toLowerCase(),
                                address: vendor.address || null, phone: vendor.phone || null, website: vendor.website || null,
                            },
                            corporateInfo: {
                                description: vendor.description || null, entityType: vendor.entityType || null,
                                registrationNumber: vendor.registrationNumber || null, pan: vendor.pan || null,
                                gst: vendor.gst || null, country: vendor.country || null,
                            },
                            bankingInfo: {
                                bankName: vendor.bankName || null, accountNumber: vendor.accountNumber || null,
                                beneficiaryName: vendor.beneficiaryName || null, branchAddress: vendor.branchAddress || null,
                                branchCode: vendor.branchCode || null, swift: vendor.swift || null,
                                paymentEmail: vendor.paymentEmail || null, specialInstructions: vendor.specialInstructions || null,
                            },
                            status: 'Active', inviteStatus: 'Used',
                            createdAt: serverTimestamp(), updatedAt: serverTimestamp()
                        });
                    }

                    if (successfulCount > 0) {
                        await batch.commit();
                    }
                    
                    setMessage(`Upload complete. Successfully added: ${successfulCount}. Skipped rows (missing data or duplicates): ${skippedCount}.`);
                    setTimeout(onClose, 4000);

                } catch (error) {
                    setMessage(`Upload failed: ${error.message}`);
                } finally {
                    setIsUploading(false);
                }
            },
            error: (error) => {
                setMessage(`Error parsing file: ${error.message}`);
                setIsUploading(false);
            }
        });
    };

    return (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg">
                <h2 className="text-xl font-bold text-[#213368] mb-4">Bulk Upload Vendor CSV</h2>
                <p className="text-sm text-gray-600 mb-4">
                    Upload a CSV file with user-friendly headers. The file you download from this system can be used as a template for a full data upload.
                </p>
                <input type="file" accept=".csv" onChange={handleFileChange} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#20BEC6] file:text-white hover:file:bg-opacity-80"/>

                {message && <p className={`mt-4 text-sm text-center ${message.includes('failed') || message.includes('Error') ? 'text-red-600' : 'text-gray-800'}`}>{message}</p>}

                <div className="flex justify-end space-x-4 mt-6">
                    <button onClick={onClose} disabled={isUploading} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 disabled:opacity-50">Cancel</button>
                    <button onClick={handleUpload} disabled={!file || isUploading} className="flex items-center justify-center px-6 py-2 w-32 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:bg-green-400 disabled:cursor-not-allowed">
                        {isUploading ? <LoadingSpinner size="sm" /> : 'Upload'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function RejectVendorModal({ vendor, onClose, onReject }) {
    const [reason, setReason] = useState('');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
                <h2 className="text-xl font-bold text-red-600 mb-4">Reject Vendor Request</h2>
                <p className="text-gray-600 mb-4">Please provide a reason for rejecting the vendor: <strong>{vendor.companyName}</strong>.</p>
                <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows="4"
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="Rejection comments..."
                ></textarea>
                <div className="flex justify-end space-x-4 mt-6">
                    <button onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">Cancel</button>
                    <button onClick={() => onReject(vendor, reason)} className="px-6 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700">Confirm Rejection</button>
                </div>
            </div>
        </div>
    );
}

